import { useRouter } from 'next/router'
import { PTC_TAG_TYPE } from '@/analytics/raven/ravenConstants'
import { trackRumEvent, getSrpRumContext, RUM_EVENT, RUM_FAILURE_REASON } from '@/analytics/sparkloom'
import { SENTRY_METRICS_CONFIG } from '@/constants/analytics/sentryCountMetrics'
import { WOW_PRICE_CT_DT_EXPERIMENT_KEY } from '@/constants/flights'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { selectExperiment } from '@/redux/slices/statsig'
import { useAppSelector } from '@/redux/store/hooks'

import { ravenSDKSrpCfwTrigger, ravenSDKTrigger } from '@/analytics/raven/ravenSDKHelper'
import { createItineraryApi } from '@/api/srp'
import { cabinClassToLabel } from '@/constants/flights/cabinClass'
import { HTTPResponseError } from '@/types/network/common'
import { countMetric, getSrpMetricAttributes } from '@/utils/analytics/sentry/metrics'
import { getFareCabin } from '@/utils/flights/cabinTabs'
import { applyCheckoutFlowHeader } from '@/utils/flights/checkoutFlow'
import { Card, Fare, FareType, QueryParamsData, SrpFareObject } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import { processQueryParams } from '@/utils/srp'
import { getCouponDisplayAndDiscountValue, getCreateCallPayloadForCfw, getFaresInfo, getFFCookieValue, getIntlTuppleTags, getPtcTagData } from '@/utils/srp/helper'
import usePremiumEconomyExperiment from '@/hooks/flights/usePremiumEconomyExperiment'
// purpose: consume data directly from redux store (where possible) - reduces code, improves readability
const useCreateItinerary = () => {
    const router = useRouter()
    const processedData = processQueryParams(router, router.query as unknown as QueryParamsData)
    const { searchData, couponData } = useAppSelector(getSrpSearchData)
    const { metaData, travelOptionIdsToFareIdsMap, dataId, fares, subTravelOptions } = searchData
    const { isCfw, isIntl, returnDate, travellers } = processedData
    const seatRecoVariant = useAppSelector(selectExperiment(isIntl ? 'new_seat_reco_int' : 'new_seat_reco_dom'))
    const isSeatRecCheapRange = seatRecoVariant === 'test2'
    const isRT = !!returnDate
    const showPerAdultPricing = travellers.adults + travellers.children + travellers.infants === 1
    const wowPricingCtDtVariant = useAppSelector(selectExperiment(WOW_PRICE_CT_DT_EXPERIMENT_KEY))
    const { API_ITINERARY_CREATE_ERROR, FUNNEL_BOOKING_INITIATED } = SENTRY_METRICS_CONFIG
    const { isPeTestVariant } = usePremiumEconomyExperiment()

    const triggerCreateItinRavenEvents = ({
        selectedFares,
        cardDetails,
        defaultCoupon,
        couponDiscountAmount,
        tuppleIndex,
        type,
        isSpecialRT
    }: {
        selectedFares: FareType[]
        cardDetails: Card[]
        defaultCoupon: string
        couponDiscountAmount: number
        tuppleIndex?: number
        type: 'continue' | 'flightDetails' | 'interstitial'
        isSpecialRT: boolean
    }) => {
        const flightNumberValue: string[] = []
        const airlineNamesValue: string[] = []
        cardDetails.map(card => {
            const { summary = { flights: [] } } = card || {}
            const { flights = [] } = summary || { flights: [] }
            flights.map((flight: any) => {
                flightNumberValue.push(`${flight.airlineCode}-${flight.flightNumber}`)
                airlineNamesValue.push(metaData.airlineDetail?.[flight.airlineCode]?.name)
            })
        })
        const fareTypes: string[] = []
        selectedFares.map(fare => {
            fareTypes.push(fare?.displayText?.displayTitle || '')
        })
        const onwardFaresCount = getFaresInfo(cardDetails[0], subTravelOptions, fares).length
        const returnFaresCount = isRT ? getFaresInfo(cardDetails[1], subTravelOptions, fares).length : -1
        const faresCount = [onwardFaresCount]
        if (returnFaresCount !== -1) faresCount.push(returnFaresCount)
        const onwardSelectedFareIndex = subTravelOptions[cardDetails[0].travelOptionId].fareList.findIndex(f => f.fareId === selectedFares[0].fareId)
        const returnSelectedFareIndex = isRT ? subTravelOptions[cardDetails[0].travelOptionId].fareList.findIndex(f => f.fareId === selectedFares[0].fareId) : -1
        const selectedFareIndex = [onwardSelectedFareIndex]
        if (returnSelectedFareIndex !== -1) selectedFareIndex.push(returnSelectedFareIndex)

        // Cabin actually booked, not the searched one. The cross-cabin rule keeps both
        // RT legs in the same cabin, so the onward leg is authoritative.
        const bookedCabin = getFareCabin(selectedFares?.[0]) || getFareCabin(selectedFares?.[1])
        const ravenPayload = {
            action_name: 'cfw_fare_selection_menue_continue_click',
            action_type: 'click',
            page_name: 'flights_srp',
            a_class_choose: (bookedCabin && cabinClassToLabel[bookedCabin]) || 'N/A',
            a_flight_tuple_num: typeof tuppleIndex === "number" ? tuppleIndex + 1 : 'N/A',
            a_flight_number: flightNumberValue.join(',') || 'N/A',
            a_airline: airlineNamesValue.join(',') || 'N/A',
            a_fare_price:
                (selectedFares?.[0]?.pricing?.totalPricing?.totalPrice || 0) +
                (selectedFares?.[1]?.pricing?.totalPricing?.totalPrice || 0),
            index: isEmpty(selectedFareIndex) ? 'N/A' : selectedFareIndex.join(','),
            available_fares: faresCount.join(',') || 0,
            a_fare_type: fareTypes.join(',') || 'N/A',
            a_srp_ff_cookie: getFFCookieValue()
        }

        const { pricing = {} } = (selectedFares?.[0] || {}) as any
        const pricingObject = showPerAdultPricing ? pricing?.perAdultPricing : pricing?.totalPricing
        const { couponDetails } = pricingObject || {}
        const { discountAmount } = couponDetails || {}
        const eventDescriptionOw = `OW: ${(getIntlTuppleTags(selectedFares?.[0]) || [])?.map((item) => item.displayText)?.join(",")}`
        const eventDescriptionRt = `RT: ${(getIntlTuppleTags(selectedFares?.[1]) || [])?.map((item) => item.displayText)?.join(",")}`
        const ptcTagDataOw = getPtcTagData(selectedFares?.[0]) || {}
        const ptcTagDataRt = getPtcTagData(selectedFares?.[1]) || {}
        const tuppleTypeOw = !isEmpty(ptcTagDataOw) ? PTC_TAG_TYPE[ptcTagDataOw.benefitType as keyof typeof PTC_TAG_TYPE] : 'N/A'
        const tuppleTypeRt = !isEmpty(ptcTagDataRt) ? PTC_TAG_TYPE[ptcTagDataRt.benefitType as keyof typeof PTC_TAG_TYPE] : 'N/A'
        const tuppleType = isRT ? [tuppleTypeOw, tuppleTypeRt].join(',') : tuppleTypeOw
        const srpBookPayload = {
            action_name: 'search_flight_srp',
            a_ct_discount: (!isRT && selectedFares?.[0] ? discountAmount : couponDiscountAmount) || 0,
            a_coupons_shown: defaultCoupon || 'N/A',
            currency: 'INR',
            event_description2: `${eventDescriptionOw}|${eventDescriptionRt}`,
            property: type === 'flightDetails' ? 'flight_details' : 'srp_bottom_bar_continue',
            spl_rt_flight: isSpecialRT ? 'yes' : 'no',
            a_tupple_type: tuppleType
        }

        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, { ...ravenPayload })
        const srpEventName = type === 'continue' ? 'a_srp_tupple_click' : 'a_srp_book_click'
        ravenSDKSrpCfwTrigger(srpEventName, searchData, processedData, { ...ravenPayload, ...srpBookPayload })
    }

    async function createItinerary({
        selectedFares,
        cardDetails,
        tuppleIndex,
        supercoinVariant = "",
        isFFFlow = false,
        isSpecialRT = false,
        type = 'continue'
    }: {
        selectedFares: FareType[]
        cardDetails: Card[]
        tuppleIndex?: number
        isFFFlow?: boolean
        supercoinVariant?: string
        isSpecialRT: boolean
        type: 'continue' | 'flightDetails' | 'interstitial'
    }) {
        let windowRef
        const errorUrl = '/flights/results/itinerary/failure'
        const startTime = Date.now()
        if (type === 'interstitial') {
            trackRumEvent(RUM_EVENT.INTERSTITIAL_BOOK_CLICKED, getSrpRumContext(processedData))
        }
        try {
            let payload = {}
            const headers: Record<string, any> = {
                dataid: dataId,
                'demand-core-enabled': 'true'
            }
            applyCheckoutFlowHeader(headers)
            const {
                couponDiscountDisplayValue,
                discountAmount: couponDiscountAmount,
                couponCode: defaultCoupon
            } = getCouponDisplayAndDiscountValue({
                selectedFares,
                showPerAdultPricing,
                isRT,
                couponData,
                cardKey: isRT ? `[${cardDetails[0].travelOptionId}, ${cardDetails[1].travelOptionId}]` : undefined,
                isSpecialRT
            })

            // Read PE gate at create-time (not a loading-gated hook) so `isPE` is included
            // in metaDataContext.abFeatures when the pe_discovery_v1 cohort or the sector
            // allowlist qualifies.
            payload = getCreateCallPayloadForCfw(
                selectedFares,
                searchData,
                defaultCoupon,
                isFFFlow,
                supercoinVariant,
                processedData,
                wowPricingCtDtVariant,
                isSeatRecCheapRange,
                isPeTestVariant
            )

            if (isFFFlow) {
                triggerCreateItinRavenEvents({
                    selectedFares,
                    cardDetails,
                    defaultCoupon,
                    tuppleIndex,
                    couponDiscountAmount,
                    type,
                    isSpecialRT
                })
            }

            const url = '/flights/results/itinerary/loading'
            windowRef = window.open(`${url}`, '_blank')
            const flightsResponse: any = await createItineraryApi(payload, headers, isCfw)
            const { itineraryId } = flightsResponse?.data || {}
            if (!isEmpty(itineraryId)) {
                countMetric(FUNNEL_BOOKING_INITIATED, 1, getSrpMetricAttributes(processedData))
                trackRumEvent(RUM_EVENT.ITINERARY_CREATED, {
                    itineraryId,
                    latency: Date.now() - startTime,
                })
                const url = `/flights/itinerary/${itineraryId}/info`
                if (windowRef) {
                    windowRef.location.replace(url)
                }
            } else {
                trackRumEvent(RUM_EVENT.ITINERARY_CREATE_FAILED, {
                    reason: RUM_FAILURE_REASON.EMPTY_ITINERARY_ID,
                    statusCode: 200,
                    latency: Date.now() - startTime,
                })
                ravenSDKTrigger('a_pageload_failure', {
                    action_name: 'a_itinerary_page_failure',
                    page_name: 'flights_itinerary',
                    a_failure_reason: 'create itinerary id error'
                })
                countMetric(API_ITINERARY_CREATE_ERROR, 1, {
                    ...getSrpMetricAttributes(processedData),
                    error_type: 'EMPTY_ITINERARY_ID',
                    errorSource: 'useCreateItinerary',
                    responseStatus: 200,
                })
                if (windowRef) {
                    windowRef.location.replace(errorUrl)
                }
            }
        } catch (e) {
            trackRumEvent(RUM_EVENT.ITINERARY_CREATE_FAILED, {
                reason: RUM_FAILURE_REASON.EXCEPTION,
                statusCode: e instanceof HTTPResponseError ? e.errorCode : null,
                latency: Date.now() - startTime,
            })
            countMetric(API_ITINERARY_CREATE_ERROR, 1, {
                ...getSrpMetricAttributes(processedData),
                error_type: 'CREATE_ITINERARY_EXCEPTION',
                errorSource: 'useCreateItinerary-catchBlock',
                responseStatus: e instanceof HTTPResponseError ? e.errorCode : 'N/A',
            })
            ravenSDKTrigger('a_pageload_failure', {
                action_name: 'a_itinerary_page_failure',
                page_name: 'flights_itinerary',
                a_failure_reason: `create itinerary api error`
            })
            console.log(e)
            if (windowRef) {
                windowRef.location.replace(errorUrl)
            }
        }
    }

    return createItinerary
}

export default useCreateItinerary